/**
 * **S3Peer —— 房间级的连接层。**
 *
 * ## 生命周期：随**房间**，不随对局
 *
 * ```
 * joinRoom()  →  createS3Peer()  ──────────────────────────►  destroy()
 *                       │
 *                       ├── 第 1 局：openSession(peer, …) → close()
 *                       ├── 第 2 局：openSession(peer, …) → close()
 *                       └── 第 N 局：…
 * ```
 *
 * ⚠️ **它不许持有任何"这一局的"数据。** 一旦某个字段里存了 `state`，
 * "复用"就变成了"泄漏"。这条规矩换来的是：换一局游戏不必重建 WebRTC 连接，
 * 而重连（本来就要重建连接）也不必重开大厅。
 *
 * ## 它把三件事拼在一起
 *
 * | | 来自 | 职责 |
 * | --- | --- | --- |
 * | `Transport` | `trystero.ts`（或测试里的内存假实现） | 字节怎么出去 |
 * | `Rpc` | `rpc.ts` | 信封怎么分派、怎么校验、怎么报障 |
 * | `Heartbeat` | `heartbeat.ts` | 对端还在不在、有多快 |
 *
 * 这四层各自都能单独测，`S3Peer` 本身只是**接线**。
 *
 * ## 为什么 RTT 要在这里做成"矩阵"
 *
 * 因为房主选举在房间里发生，而房间是这一层的生命周期。
 * 每个 peer 每 tick 把自己**那一行**（我到所有人的 RTT）广播出去，
 * 于是每个人都能拼出完整矩阵，`electHost()` 就能是纯函数。
 * 详见 `docs/netcode-s3.md` §3.2。
 */

import { z } from 'zod';
import { t } from '../i18n';
import { createHeartbeat, type Heartbeat, type PeerLiveness, type RttRow } from './heartbeat';
import type { RttMatrix } from './election';
import { createRpc, type PardonReport, type RpcContext } from './rpc';
import type { Transport, Unsubscribe } from './transport';

/** 心跳附带的 RTT 行。**它是 S3Peer 自己的方法**，不属于任何 Session */
export const HEARTBEAT_METHOD = 's3/hb';

const HeartbeatSchema = z.object({
  rtts: z.record(z.string(), z.number()),
});

/**
 * `S3Session` 需要的最小接口。
 *
 * 刻意**不是** `S3Peer` 本身：`session.ts` 只依赖这个接口，于是测试里
 * 可以拿一个手工拼的 channel 去驱动它，不必先起一套心跳和名册。
 * 这也是"依赖倒置"在这里的具体好处 —— 它让 `session.ts` 的测试
 * 完全不用关心 Trystero、不用关心定时器。
 */
export interface S3Channel {
  readonly selfId: string;
  peers(): readonly string[];
  onPeerJoin(handler: (id: string) => void): Unsubscribe;
  onPeerLeave(handler: (id: string) => void): Unsubscribe;
  /** 心跳判定的存活变化。`up` 是"复活"，`down` 是"判死" */
  onPeerState(handler: (id: string, state: PeerLiveness) => void): Unsubscribe;

  request<T>(to: string, method: string, payload: unknown, schema: z.ZodType<T>, timeoutMs?: number): Promise<T>;
  notify(method: string, payload: unknown, to?: string | null): void;
  onRequest<T>(method: string, schema: z.ZodType<T>, handler: (payload: T, ctx: RpcContext) => unknown | Promise<unknown>): Unsubscribe;
  onNotify<T>(method: string, schema: z.ZodType<T>, handler: (payload: T, from: string) => void): Unsubscribe;

  /** 观测用：远端报障单与本地异常 */
  onPardon(handler: (from: string, pardon: PardonReport) => void): Unsubscribe;
  onError(handler: (error: unknown) => void): Unsubscribe;
}

export interface S3PeerOptions {
  readonly transport: Transport;
  /** 心跳间隔。默认 3000ms */
  readonly heartbeatMs?: number;
  /** 连续几次心跳失败算死。默认 3 */
  readonly missThreshold?: number;
  /** 请求默认超时。默认 8000ms */
  readonly requestTimeoutMs?: number;
  /** 是否立刻开始心跳。默认 `true`；测试里可以关掉手动 `tick()` */
  readonly autoStart?: boolean;
  readonly onError?: (error: unknown) => void;
  readonly onPardon?: (from: string, pardon: PardonReport) => void;
  readonly log?: (message: string) => void;
}

export interface S3Peer extends S3Channel {
  /** 平滑后的 RTT，还没测出来时为 `null` */
  rtt(peerId: string): number | null;
  alive(peerId: string): boolean;
  /** 完整 RTT 矩阵（含我自己那一行）。喂给 `electHost()` */
  rttMatrix(): RttMatrix;
  /** 心跳本身。诊断与测试用 —— **业务代码不该碰它** */
  readonly heartbeat: Heartbeat;
  destroy(): void;
}

export function createS3Peer(options: S3PeerOptions): S3Peer {
  const { transport } = options;
  const log = options.log ?? ((message: string) => console.warn(`[s3] ${message}`));

  const rpc = createRpc(transport, {
    defaultTimeoutMs: options.requestTimeoutMs,
    ...(options.onError === undefined ? {} : { onError: options.onError }),
    onPardon: (from, pardon) => {
      // 报障单是**可观测性**，不是错误恢复路径：记下来，继续跑
      log(t('host.peer.pardonReceived', { from, reason: pardon.reason }));
      options.onPardon?.(from, pardon);
    },
  });

  /** 别人上报的 RTT 行。我自己的那一行由心跳维护，不在这里 */
  const remoteRows = new Map<string, RttRow>();

  const peerStateListeners = new Set<(id: string, state: PeerLiveness) => void>();
  const peerJoinListeners = new Set<(id: string) => void>();
  const peerLeaveListeners = new Set<(id: string) => void>();

  const emitPeerState = (id: string, state: PeerLiveness): void => {
    for (const listener of peerStateListeners) listener(id, state);
  };

  const heartbeat = createHeartbeat({
    ping: (peerId, timeoutMs) => transport.ping(peerId, timeoutMs),
    peers: () => transport.peers(),
    ...(options.heartbeatMs === undefined ? {} : { intervalMs: options.heartbeatMs }),
    ...(options.missThreshold === undefined ? {} : { missThreshold: options.missThreshold }),
    onTick: (row) => {
      // 每 tick 广播一次**我这一行**。≤5 人的房间里这只是几个数字
      rpc.notify(HEARTBEAT_METHOD, { rtts: row });
    },
    onStateChange: emitPeerState,
    onMiss: (peerId, misses, error) => {
      log(t('host.peer.heartbeatMiss', { peer: peerId, n: misses, error: describeShort(error) }));
    },
  });

  const unsubscribers: Unsubscribe[] = [];
  unsubscribers.push(
    rpc.onNotify(HEARTBEAT_METHOD, HeartbeatSchema, (payload, from) => {
      const parsed = payload as { rtts: RttRow };
      remoteRows.set(from, parsed.rtts);
    }),
  );

  unsubscribers.push(
    transport.onPeerJoin((id) => {
      // 立刻测一次，别等下一 tick —— 新来的人越早出现在选举数据里越好
      heartbeat.note(id);
      void heartbeat.tick();
      for (const listener of peerJoinListeners) listener(id);
    }),
  );

  unsubscribers.push(
    transport.onPeerLeave((id) => {
      heartbeat.forget(id);
      remoteRows.delete(id);
      for (const listener of peerLeaveListeners) listener(id);
    }),
  );

  let destroyed = false;

  if (options.autoStart !== false) heartbeat.start();

  return {
    selfId: transport.selfId,

    peers: () => transport.peers(),

    onPeerJoin(handler) {
      peerJoinListeners.add(handler);
      return () => peerJoinListeners.delete(handler);
    },

    onPeerLeave(handler) {
      peerLeaveListeners.add(handler);
      return () => peerLeaveListeners.delete(handler);
    },

    onPeerState(handler) {
      peerStateListeners.add(handler);
      return () => peerStateListeners.delete(handler);
    },

    request: (to, method, payload, schema, timeoutMs) => rpc.request(to, method, payload, schema, timeoutMs),
    notify: (method, payload, to) => rpc.notify(method, payload, to),
    onRequest: (method, schema, handler) => rpc.handle(method, schema, handler),
    onNotify: (method, schema, handler) => rpc.onNotify(method, schema, handler),
    onPardon: (handler) => rpc.onPardon(handler),
    onError: (handler) => rpc.onError(handler),

    rtt: (peerId) => heartbeat.rtt(peerId),
    alive: (peerId) => heartbeat.alive(peerId),

    rttMatrix() {
      const matrix: Record<string, RttRow> = { [transport.selfId]: heartbeat.row() };
      for (const [peerId, row] of remoteRows) {
        if (peerId === transport.selfId) continue;
        matrix[peerId] = row;
      }
      return matrix;
    },

    heartbeat,

    destroy() {
      if (destroyed) return;
      destroyed = true;
      heartbeat.stop();
      for (const unsubscribe of unsubscribers) unsubscribe();
      rpc.dispose();
      transport.destroy();
    },
  };
}

function describeShort(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}
